import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Result, Typography, Space, message } from 'antd';
import { ReloadOutlined, CopyOutlined, DeleteOutlined } from '@ant-design/icons';

const { Paragraph, Text } = Typography;

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] 捕获到未处理的 React 渲染错误:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleClearCacheAndReload = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      console.error('清除缓存失败:', e);
    }
    window.location.href = window.location.pathname + '?_t=' + Date.now();
  };

  handleCopyError = () => {
    const errorDetails = [
      `【错误名称】: ${this.state.error?.name || 'Error'}`,
      `【错误消息】: ${this.state.error?.message || '未知错误'}`,
      `【UA 信息】: ${navigator.userAgent}`,
      `【当前 URL】: ${window.location.href}`,
      `【调用栈】:`,
      this.state.error?.stack || '无堆栈信息',
      `【组件堆栈】:`,
      this.state.errorInfo?.componentStack || '无组件堆栈',
    ].join('\n');

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(errorDetails)
        .then(() => message.success('错误详情已复制到剪贴板'))
        .catch(() => this.fallbackCopy(errorDetails));
    } else {
      this.fallbackCopy(errorDetails);
    }
  };

  fallbackCopy = (text: string) => {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      message.success('错误详情已复制到剪贴板');
    } catch (e) {
      message.error('复制失败，请手动截图或在电脑端访问');
    }
  };

  render() {
    if (this.state.hasError) {
      const errorMessage = this.state.error?.message || '页面加载或渲染时发生意外错误';
      const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '16px 12px' : '40px 24px',
            backgroundColor: '#f5f5f5',
          }}
        >
          <div
            style={{
              maxWidth: 640,
              width: '100%',
              backgroundColor: '#fff',
              borderRadius: 12,
              padding: isMobile ? '20px 16px' : '32px 28px',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Result
              status="warning"
              title="页面遇到异常未能正常显示"
              subTitle="可能是由于网络波动、版本更新或手机浏览器兼容性问题引起"
              extra={
                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                  <Space orientation="horizontal" wrap style={{ justifyContent: 'center', width: '100%' }}>
                    <Button type="primary" icon={<ReloadOutlined />} onClick={this.handleReload} size={isMobile ? 'middle' : 'large'}>
                      重新加载
                    </Button>
                    <Button icon={<DeleteOutlined />} onClick={this.handleClearCacheAndReload} size={isMobile ? 'middle' : 'large'}>
                      清除缓存并刷新
                    </Button>
                    <Button icon={<CopyOutlined />} onClick={this.handleCopyError} size={isMobile ? 'middle' : 'large'}>
                      复制错误信息
                    </Button>
                  </Space>
                  <div
                    style={{
                      marginTop: 16,
                      textAlign: 'left',
                      backgroundColor: '#f9f9f9',
                      padding: 12,
                      borderRadius: 6,
                      border: '1px solid #e8e8e8',
                      maxHeight: 180,
                      overflowY: 'auto',
                    }}
                  >
                    <Text type="danger" strong style={{ fontSize: 13 }}>
                      错误摘要: {errorMessage}
                    </Text>
                    {this.state.error?.stack && (
                      <Paragraph
                        code
                        style={{
                          fontSize: 11,
                          marginTop: 8,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          color: '#666',
                        }}
                      >
                        {this.state.error.stack}
                      </Paragraph>
                    )}
                  </div>
                </Space>
              }
            />
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
